import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, KeyRound, Trash2, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useSettingsStore } from '../../stores/settings-store';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { useAiAccessStore } from '../../stores/ai-access-store';
import { DebugLogPanel } from './DebugLogPanel';

export function SettingsDialog() {
  const open = useSettingsStore((s) => s.settingsOpen);
  const setSettingsOpen = useSettingsStore((s) => s.setSettingsOpen);

  const provider = useAiAccessStore((s) => s.provider);
  const setProvider = useAiAccessStore((s) => s.setProvider);
  const setOpenRouterModel = useAiAccessStore((s) => s.setOpenRouterModel);

  const hasKey = useAiAccessStore((s) => s.hasKey); // active provider

  const geminiHasKey = useAiAccessStore((s) => s.geminiHasKey);
  const geminiMaskedKey = useAiAccessStore((s) => s.geminiMaskedKey);
  const openrouterHasKey = useAiAccessStore((s) => s.openrouterHasKey);
  const openrouterMaskedKey = useAiAccessStore((s) => s.openrouterMaskedKey);
  const openrouterModel = useAiAccessStore((s) => s.openrouterModel);

  const dailyLimit = useAiAccessStore((s) => s.dailyLimit);
  const usedToday = useAiAccessStore((s) => s.usedToday);
  const remainingToday = useAiAccessStore((s) => s.remainingToday);
  const tokenInput = useAiAccessStore((s) => s.tokenInput);
  const tokenOutput = useAiAccessStore((s) => s.tokenOutput);
  const tokenTotal = useAiAccessStore((s) => s.tokenTotal);

  const loading = useAiAccessStore((s) => s.loading);
  const saving = useAiAccessStore((s) => s.saving);
  const deleting = useAiAccessStore((s) => s.deleting);
  const error = useAiAccessStore((s) => s.error);

  const refreshStatus = useAiAccessStore((s) => s.refreshStatus);
  const saveGeminiKey = useAiAccessStore((s) => s.saveGeminiKey);
  const removeGeminiKey = useAiAccessStore((s) => s.removeGeminiKey);
  const saveOpenRouterKey = useAiAccessStore((s) => s.saveOpenRouterKey);
  const removeOpenRouterKey = useAiAccessStore((s) => s.removeOpenRouterKey);
  const clearError = useAiAccessStore((s) => s.clearError);

  const [tab, setTab] = useState<'gemini' | 'openrouter'>('gemini');
  const [draftGeminiKey, setDraftGeminiKey] = useState('');
  const [draftOpenRouterKey, setDraftOpenRouterKey] = useState('');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showOpenRouterKey, setShowOpenRouterKey] = useState(false);

  useEffect(() => {
    if (open) void refreshStatus();
  }, [open, refreshStatus]);

  useEffect(() => {
    setTab(provider);
  }, [provider]);

  const usageProgress = dailyLimit > 0 ? Math.min(100, Math.round((usedToday / dailyLimit) * 100)) : 0;

  const activeLabel = tab === 'openrouter' ? 'OpenRouter' : 'Gemini';
  const activeHasKey = tab === 'openrouter' ? openrouterHasKey : geminiHasKey;
  const activeMasked = tab === 'openrouter' ? openrouterMaskedKey : geminiMaskedKey;
  const activeDraft = tab === 'openrouter' ? draftOpenRouterKey : draftGeminiKey;
  const activeShow = tab === 'openrouter' ? showOpenRouterKey : showGeminiKey;

  const openRouterModelOptions = useMemo(
    () => [
      { id: 'gpt-5-nano-2025-08-07' as const, label: 'GPT-5 Nano' },
      { id: 'openai/gpt-5-mini' as const, label: 'GPT-5 Mini' },
      { id: 'z-ai/glm-4.7-flash' as const, label: 'Z.ai GLM 4.7 Flash' },
      { id: 'qwen/qwen-2.5-7b-instruct' as const, label: 'Qwen 2.5 7B' },
      { id: 'anthropic/claude-3.5-sonnet' as const, label: 'Claude 3.5 Sonnet' },
      { id: 'anthropic/claude-3-haiku' as const, label: 'Claude 3 Haiku' },
      { id: 'google/gemini-2.0-flash-001' as const, label: 'Gemini 2.0 Flash' },
      { id: 'google/gemini-3-flash-preview' as const, label: 'Gemini 3 Flash' },
      { id: 'openai/gpt-4o-mini' as const, label: 'GPT-4o Mini' },
      { id: 'openai/gpt-4o' as const, label: 'GPT-4o' },
    ],
    []
  );

  const handleSave = async () => {
    try {
      if (tab === 'openrouter') {
        await saveOpenRouterKey(draftOpenRouterKey);
        setDraftOpenRouterKey('');
        setShowOpenRouterKey(false);
      } else {
        await saveGeminiKey(draftGeminiKey);
        setDraftGeminiKey('');
        setShowGeminiKey(false);
      }
    } catch {
      // store already has error
    }
  };

  const handleDelete = async () => {
    try {
      if (tab === 'openrouter') {
        await removeOpenRouterKey();
        setDraftOpenRouterKey('');
        setShowOpenRouterKey(false);
      } else {
        await removeGeminiKey();
        setDraftGeminiKey('');
        setShowGeminiKey(false);
      }
    } catch {
      // store already has error
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        clearError();
        setSettingsOpen(false);
      }}
      title="AI Access"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 rounded-lg border border-surface-lighter bg-surface p-1">
          <button
            type="button"
            className={clsx(
              'flex-1 h-9 rounded-md text-xs font-semibold transition-colors',
              tab === 'gemini' ? 'bg-surface-lighter text-text' : 'text-text-muted hover:text-text'
            )}
            onClick={async () => {
              setTab('gemini');
              try {
                await setProvider('gemini');
              } catch {}
            }}
            disabled={saving || deleting}
          >
            Gemini
          </button>
          <button
            type="button"
            className={clsx(
              'flex-1 h-9 rounded-md text-xs font-semibold transition-colors',
              tab === 'openrouter' ? 'bg-surface-lighter text-text' : 'text-text-muted hover:text-text'
            )}
            onClick={async () => {
              setTab('openrouter');
              try {
                await setProvider('openrouter');
              } catch {}
            }}
            disabled={saving || deleting}
          >
            OpenRouter
          </button>
        </div>

        <div className="rounded-md border border-surface-lighter bg-surface p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-muted">{activeLabel} Key</span>
            <span
              className={clsx(
                'text-[10px] px-2 py-0.5 rounded',
                activeHasKey ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
              )}
            >
              {activeHasKey ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          <p className="mt-1 text-xs text-text">
            {activeHasKey ? `Stored key: ${activeMasked}` : `Add your ${activeLabel} API key to enable generation.`}
          </p>
          <p className="mt-1 text-[10px] text-text-muted">
            Key is encrypted on server and never stored in browser local storage.
          </p>
          {loading && <p className="mt-1 text-[10px] text-text-muted">Refreshing status...</p>}
        </div>

        {tab === 'openrouter' ? (
          <div className="rounded-md border border-surface-lighter bg-surface p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted">Token usage (last request)</span>
              <span className="text-text">{tokenTotal}</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded bg-surface-light border border-surface-lighter p-2">
                <div className="text-text-muted">Input</div>
                <div className="mt-0.5 text-text font-semibold tabular-nums">{tokenInput}</div>
              </div>
              <div className="rounded bg-surface-light border border-surface-lighter p-2">
                <div className="text-text-muted">Output</div>
                <div className="mt-0.5 text-text font-semibold tabular-nums">{tokenOutput}</div>
              </div>
              <div className="rounded bg-surface-light border border-surface-lighter p-2">
                <div className="text-text-muted">Total</div>
                <div className="mt-0.5 text-text font-semibold tabular-nums">{tokenTotal}</div>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-text-muted">
              OpenRouter is unlimited in this app (no 20/day prompt limit).
            </p>
          </div>
        ) : hasKey ? (
          <div className="rounded-md border border-surface-lighter bg-surface p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted">Daily Usage</span>
              <span className="text-text">
                {usedToday}/{dailyLimit}
              </span>
            </div>
            <div className="mt-2 h-2 w-full rounded bg-surface-lighter overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${usageProgress}%` }} />
            </div>
            <p className="mt-2 text-[10px] text-text-muted">Remaining today: {remainingToday}</p>
          </div>
        ) : (
          <div className="rounded-md border border-surface-lighter bg-surface p-3 text-xs text-text-muted">
            Подключите ключ выбранного провайдера, чтобы включить генерацию.
          </div>
        )}

        {tab === 'openrouter' && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-text">Model</label>
            <select
              className="w-full px-3 py-2 bg-surface border border-surface-lighter rounded text-sm text-text focus:outline-none focus:border-accent"
              value={openrouterModel}
              onChange={async (e) => {
                const next = e.target.value as any;
                try {
                  await setOpenRouterModel(next);
                } catch {}
              }}
              disabled={saving || deleting}
            >
              {openRouterModelOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-text-muted">This model will be used for generation via OpenRouter.</p>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-text">
            {activeLabel} API Key <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <input
              type={activeShow ? 'text' : 'password'}
              value={activeDraft}
              onChange={(e) => {
                if (tab === 'openrouter') setDraftOpenRouterKey(e.target.value);
                else setDraftGeminiKey(e.target.value);
              }}
              placeholder={tab === 'openrouter' ? 'sk-or-v1-... (paste your OpenRouter API key)' : 'AIza... (paste your Gemini API key)'}
              autoComplete="off"
              disabled={saving || deleting}
              className="w-full px-3 py-2 pr-10 bg-surface border border-accent/30 rounded text-sm text-text placeholder:text-text-muted/50 focus:outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={() => {
                if (tab === 'openrouter') setShowOpenRouterKey((v) => !v);
                else setShowGeminiKey((v) => !v);
              }}
              disabled={saving || deleting}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
            >
              {activeShow ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-[10px] text-text-muted">
            Get your key at{' '}
            <a
              href={tab === 'openrouter' ? 'https://openrouter.ai/keys' : 'https://aistudio.google.com/apikey'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              {tab === 'openrouter' ? 'openrouter.ai/keys' : 'aistudio.google.com/apikey'}
            </a>
          </p>
        </div>

        {error && <div className="rounded bg-red-900/30 text-red-300 text-xs px-3 py-2">{error}</div>}

        <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
          {activeHasKey && (
            <Button
              variant="secondary"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="inline-flex items-center justify-center min-w-36 h-11"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              <span className="ml-1.5">Remove Key</span>
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setSettingsOpen(false)}
            disabled={saving || deleting}
            className="inline-flex items-center justify-center min-w-24 h-11"
          >
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={!activeDraft.trim() || saving || deleting}
            className="inline-flex items-center justify-center min-w-36 h-11"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            <span className="ml-1.5">{activeHasKey ? 'Update Key' : 'Connect Key'}</span>
          </Button>
        </div>

        <DebugLogPanel />
      </div>
    </Dialog>
  );
}
